<template>
  <div
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDragLeave"
  >
    <v-textarea
      v-model="newNote"
      :rows="3"
      :placeholder="placeholder"
      variant="outlined"
      density="compact"
      hide-details
    ></v-textarea>
    <!-- New file upload on new note, only shows when drag is initiated. -->
    <asset-inline-upload
      v-show="showFileUploadOnHover ? isDrag : true"
      :asset-types="['image', 'pdf', 'video', 'audio']"
      @upload-complete="handleAssetAdd"
      @upload-error="handleUploadError"
    ></asset-inline-upload>

    <div v-if="useCompactButtons" class="note-new-compact-container">
      <div class="note-new-button-group">
        <v-tooltip text="Upload file" location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              size="small"
              @click="showAssetUpload = !showAssetUpload"
              variant="text"
            >
              <v-icon>mdi-upload</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <content-block-select
          v-if="showQuickReply"
          :use-menu="true"
          :replyType="'text'"
          @updateContent="mergeMessage"
          :size="'small'"
          :use-vuetify="true"
        ></content-block-select>
        <v-tooltip text="Quick reply" location="top">
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="getTextBlocks.length"
              v-bind="props"
              size="small"
              @click="showQuickReply = !showQuickReply"
              variant="text"
            >
              <v-icon>mdi-message-reply-text</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </div>
      <v-btn
        size="small"
        variant="tonal"
        color="primary"
        @click="saveNewNotes"
        :icon="true"
      >
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </div>

    <v-row v-else justify="end" align="center" class="mt-3 mb-2 mr-1">
      <content-block-select
        v-if="showQuickReply"
        :use-menu="true"
        :replyType="'text'"
        :size="'small'"
        @updateContent="mergeMessage"
      >
      </content-block-select>
      <v-btn
        v-if="getTextBlocks.length"
        size="small"
        @click="showQuickReply = !showQuickReply"
      >
        <v-icon>mdi-comment</v-icon>
        Quick Reply
      </v-btn>

      <template v-if="showCancel">
        <v-divider vertical></v-divider>
        <v-btn size="small" @click="cancelNote" variant="text"> Cancel </v-btn>
      </template>

      <v-divider vertical></v-divider>

      <v-btn size="small" @click="showAssetUpload = !showAssetUpload">
        <v-icon>mdi-upload</v-icon>
        Upload Asset
      </v-btn>

      <v-btn size="small" @click="saveNewNotes" class="ml-2"> Save </v-btn>
    </v-row>
    <assets-grid
      v-if="attachFiles.length > 0"
      :assets="attachFiles"
      :allow-delete="true"
      @delete-asset="(asset) => handleNewAssetDelete(asset)"
    ></assets-grid>
    <el-dialog :title="'Select File'" v-model="showAssetUpload">
      <asset-library
        :asset-types="['image', 'pdf', 'audio', 'video']"
        @update="handleAssetAdd"
        @close="showAssetUpload = false"
        :thumbnail-mode="true"
        :limit="5"
      ></asset-library>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { uploadAsset } from 'src/util/assetUtils.js';

const emit = defineEmits(['newNote']);

import AssetLibrary from 'src/components/UIComponents/AssetLibrary';
import AssetsGrid from 'src/components/Dashboard/Views/Assets/AssetsGrid.vue';
import AssetInlineUpload from 'src/components/UIComponents/AssetInlineUpload.vue';
import ContentBlockSelect from 'src/components/UIComponents/ContentBlockSelect';

import notesServices from 'src/services/notesServices';
import templateService from 'src/services/templateServices';
import SmsService from 'src/services/smsServices.js';

const store = useStore();

// computed
const getTextBlocks = computed(
  () => store.getters['contentBlocks/getTextBlocks']
);

//data

const newNote = ref(undefined);
const attachFiles = ref([]);
const isDrag = ref(false);
const showQuickReply = ref(false);
const showAssetUpload = ref(false);
const loading = ref(true);

const props = defineProps({
  // Below are provided for contact merge reasons.
  // Explicit or one object??
  contactId: {
    type: Number,
    required: false,
  },
  leadId: {
    type: Number,
    required: false,
  },
  // Task not supported on merge yet
  taskId: {
    type: Number,
    required: false,
  },
  reservationId: {
    type: Number,
    required: false,
  },
  showFileUploadOnHover: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Add a note...',
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  useCompactButtons: {
    type: Boolean,
    default: false,
  },
});

// Methods
const handleDragEnter = () => {
  if (props.showFileUploadOnHover) {
    isDrag.value = true;
  }
};

const handleDragLeave = () => {
  if (props.showFileUploadOnHover) {
    isDrag.value = false;
  }
};

const handleAssetAdd = async (asset) => {
  if (!asset?.url) {
    return;
  }

  attachFiles.value.push(asset);
  isDrag.value = false;
};
const handleUploadError = (error) => {
  console.error('Upload failed:', error);
};

const mergeMessage = async (content, edit = false) => {
  let result;
  try {
    result = await templateService.getTemplateMerge(
      0,
      props.contactId,
      undefined, // campaignId
      props.reservationId, // reservationId
      props.leadId,
      content
    );
  } catch (e) {
    if (!e.response) {
      throw e;
    }
    if (e.response.status == 422) {
      const result = e.response.data;
      this.$message({
        type: 'error',
        message:
          'Error Parsing Template at this line: <br><br>' +
            result.friendly_error ?? result.status,
        showClose: true,
        // Love me some danger
        dangerouslyUseHTMLString: true,
      });
    } else {
      this.$message({
        type: 'error',
        message: 'Unexpected Error Parsing Template',
        showClose: true,
      });
    }
    return false;
  }

  newNote.value = String(newNote.value).concat(result.data.rich_content);
};

const saveNewNotes = async () => {
  emit('newNote', {
    note: newNote.value,
    attachments: attachFiles.value,
  });

  // clear it out
  newNote.value = '';
};

const cancelNote = () => {
  emit('newNote', undefined);
};

// watch must be last
watch(
  () => props.messageId,
  (newId) => {
    // loadMsg();
  },
  { immediate: true }
);
</script>

<style>
.note-new-compact-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* margin-top: 8px; */
}

.note-new-button-group {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
